import React, { useState, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import PlusButtonMenu from './PlusButtonMenu';
import TemplateAssistantModal from './TemplateAssistantModal';

interface Props {
  onSend: (message: string) => Promise<void>;
}

const CONTROL_SIZE = 32;
const MAX_HEIGHT = 200;

const NewChatInput = ({ onSend }: Props) => {
  const { theme } = useAITTheme();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const plusButtonRef = useRef<HTMLButtonElement>(null);

  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComposing, setIsComposing] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  const styles = useMemo(() => getStyles(theme, isMultiline), [theme, isMultiline]);

  const canSend = inputValue.trim().length > 0 && !sending;

  const menuItems = useMemo(
    () => [
      {
        id: 'template-assistant',
        label: 'テンプレートアシスタント',
        icon: '☰',
        onClick: () => setTemplateModalOpen(true)
      }
    ],
    []
  );

  const handleInsertTemplate = useCallback((text: string) => {
    setInputValue(prev => (prev ? `${prev}\n${text}` : text));
    setTemplateModalOpen(false);
  }, []);

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (el === null) return;

    el.style.height = '0px';
    const next = Math.min(el.scrollHeight, MAX_HEIGHT);
    const height = Math.max(next, CONTROL_SIZE);
    el.style.height = `${height}px`;

    const multiline = height > CONTROL_SIZE + 1;
    setIsMultiline(prev => (prev !== multiline ? multiline : prev));
  }, []);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [inputValue, resizeTextarea]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (sending || text === '') return;

    setSending(true);
    setError(null);
    try {
      await onSend(text);
    } catch (e) {
      const message =
        e instanceof Error && e.message ? e.message : '送信に失敗しました。時間をおいて再度お試しください。';
      setError(message);
    } finally {
      setSending(false);
    }
  }, [inputValue, sending, onSend]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey && !isComposing) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend, isComposing]
  );

  return (
    <div style={styles.container}>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <div style={styles.inputCard}>
        <button
          ref={plusButtonRef}
          type="button"
          style={styles.plusButton}
          onClick={() => setMenuOpen(prev => !prev)}
          aria-label="メニューを開く"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          +
        </button>
        <PlusButtonMenu
          anchorEl={plusButtonRef.current}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          items={menuItems}
        />
        <div style={styles.textareaWrapper}>
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={event => setInputValue(event.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            placeholder="メッセージを入力..."
            disabled={sending}
            aria-label="メッセージを入力"
            style={{
              ...styles.textarea,
              ...(sending ? { opacity: 0.6 } : {})
            }}
          />
        </div>
        <button
          type="button"
          style={{
            ...styles.sendButton,
            ...(!canSend ? styles.sendButtonDisabled : {})
          }}
          onClick={handleSend}
          disabled={!canSend}
          aria-label="メッセージを送信"
        >
          ↑
        </button>
      </div>
      <TemplateAssistantModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onInsert={handleInsertTemplate}
      />
    </div>
  );
};

const getStyles = (theme: AITTheme, isMultiline: boolean) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    maxWidth: '720px',
    width: '100%'
  },
  inputCard: {
    display: 'flex',
    alignItems: isMultiline ? 'flex-end' : 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#ffffff',
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
  },
  plusButton: {
    width: `${CONTROL_SIZE}px`,
    height: `${CONTROL_SIZE}px`,
    minWidth: `${CONTROL_SIZE}px`,
    borderRadius: '50%',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxSizing: 'border-box' as const,
    backgroundColor: theme.contentBgColor,
    border: `1px solid ${theme.grayBorderColor}`,
    color: theme.textColor,
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: '18px',
    alignSelf: isMultiline ? 'flex-end' : 'center',
    transition: 'background-color 0.15s ease'
  },
  textareaWrapper: {
    flex: 1,
    minHeight: `${CONTROL_SIZE}px`,
    display: 'flex',
    alignItems: 'flex-end',
    position: 'relative' as const
  },
  textarea: {
    border: 'none',
    outline: 'none',
    resize: 'none' as const,
    width: '100%',
    height: `${CONTROL_SIZE}px`,
    maxHeight: `${MAX_HEIGHT}px`,
    boxSizing: 'border-box' as const,
    boxShadow: 'none',
    overflowY: 'hidden' as const,
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '20px',
    padding: '6px 0',
    margin: 0,
    backgroundColor: 'transparent'
  },
  sendButton: {
    width: `${CONTROL_SIZE}px`,
    height: `${CONTROL_SIZE}px`,
    minWidth: `${CONTROL_SIZE}px`,
    borderRadius: '50%',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxSizing: 'border-box' as const,
    backgroundColor: theme.linkColor,
    border: 'none',
    color: theme.whiteColor,
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: '14px',
    alignSelf: isMultiline ? 'flex-end' : 'center'
  },
  sendButtonDisabled: {
    backgroundColor: theme.grayBorderColor,
    cursor: 'not-allowed'
  }
});

export default NewChatInput;
